import { Preferences } from "@capacitor/preferences";
import { isNativePreferencesBackend } from "@/auth/platform";

/** Stored flags — native Preferences + web localStorage (same keys). */
export const UI_HINT_KEYS = {
  AI_DISCLOSURE_ACCEPTED: "ai_disclosure_accepted_v1",
  MESSAGE_MENU_COACH_DISMISSED: "message_menu_coach_dismissed_v1",
} as const;

async function prefRemove(key: string): Promise<void> {
  await Preferences.remove({ key });
}

async function prefSet(key: string, value: string): Promise<void> {
  await Preferences.set({ key, value });
}

async function prefGet(key: string): Promise<string | null> {
  const { value } = await Preferences.get({ key });
  return value ?? null;
}

export async function getAiDisclosureAccepted(): Promise<boolean> {
  try {
    if (isNativePreferencesBackend()) {
      return (await prefGet(UI_HINT_KEYS.AI_DISCLOSURE_ACCEPTED)) === "1";
    }
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(UI_HINT_KEYS.AI_DISCLOSURE_ACCEPTED) === "1";
  } catch {
    return false;
  }
}

export async function setAiDisclosureAccepted(): Promise<void> {
  try {
    if (isNativePreferencesBackend()) {
      await prefSet(UI_HINT_KEYS.AI_DISCLOSURE_ACCEPTED, "1");
      return;
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(UI_HINT_KEYS.AI_DISCLOSURE_ACCEPTED, "1");
    }
  } catch {
    /* ignore */
  }
}

export async function getMessageMenuCoachDismissed(): Promise<boolean> {
  try {
    if (isNativePreferencesBackend()) {
      return (await prefGet(UI_HINT_KEYS.MESSAGE_MENU_COACH_DISMISSED)) === "1";
    }
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(UI_HINT_KEYS.MESSAGE_MENU_COACH_DISMISSED) === "1";
  } catch {
    return false;
  }
}

export async function setMessageMenuCoachDismissed(): Promise<void> {
  try {
    if (isNativePreferencesBackend()) {
      await prefSet(UI_HINT_KEYS.MESSAGE_MENU_COACH_DISMISSED, "1");
      return;
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(UI_HINT_KEYS.MESSAGE_MENU_COACH_DISMISSED, "1");
    }
  } catch {
    /* ignore */
  }
}

/** Clears AI disclosure + coach-mark prefs so hints can show again (profile reset). */
export async function resetUiHintsPreferences(): Promise<void> {
  try {
    if (isNativePreferencesBackend()) {
      await prefRemove(UI_HINT_KEYS.AI_DISCLOSURE_ACCEPTED);
      await prefRemove(UI_HINT_KEYS.MESSAGE_MENU_COACH_DISMISSED);
      return;
    }
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(UI_HINT_KEYS.AI_DISCLOSURE_ACCEPTED);
      window.localStorage.removeItem(UI_HINT_KEYS.MESSAGE_MENU_COACH_DISMISSED);
    }
  } catch {
    /* ignore */
  }
}
